import { AfterViewInit, booleanAttribute, ChangeDetectionStrategy, Component, computed, ElementRef, input, OnDestroy, OnInit, output, Renderer2, signal, TemplateRef, viewChild } from '@angular/core';
import { CdkDrag, CdkDragDrop, CdkDropList, DragDropModule, moveItemInArray } from '@angular/cdk/drag-drop';
import { expandPanel_binding, expandPanel_read, expandPanelAnimation } from 'src/app/infrastructure/animations/expand-panel-animation';

import { ActivatedRouteService } from 'src/app/infrastructure/services/route-service/activated-route.service';
import { AppMaterialModule } from 'src/app/features/shared/material.module';
import { ButtonActionComponent } from 'src/app/features/shared/controls/container/button-action/button-action.component';
import { ListPositionComponent } from 'src/app/features/shared/lists/list-position/list-position.component';
import { ILevelGeneralListData } from 'src/app/infrastructure/models/level-general-list-data';
import { ILevelGeneralListDto } from 'src/app/infrastructure/models/level-general-list-dto';
import { LoadingPageService } from 'src/app/infrastructure/services/loading-services/loading-page.service';
import { LoadingTabService } from 'src/app/infrastructure/services/loading-services/loading-tab.service';
import { MatSelectionListChange } from '@angular/material/list';
import { PagerService } from 'src/app/features/shared/pager/pager.service';
import { SharedModule } from 'src/app/features/shared/shared.module';
import { TabDataService } from 'src/app/features/shared/central-page/services/tab-data.service';
import { TabListService } from 'src/app/features/shared/central-page/services/tab-list.service';
import { TablistFooterComponent } from 'src/app/features/shared/central-page/container/tablist/container/tablist-footer/tablist-footer.component';
import { _ListService } from 'src/app/infrastructure/services/list-services/_list.service';
import { _NavigationList_Base_ToolComponent } from 'src/app/infrastructure/components/tablist-components/_navigation-list-base-tool.component';
import { _PageService } from 'src/app/infrastructure/services/page-services/_page.service';
import { injectRequired } from 'src/app/infrastructure/functions/inject-required.function';
import { ProgressBarService } from 'src/app/features/shared/progress-bar/progress-bar.service';
import { listButtonData } from '../../../models/_base-list-button';
import { showAnimation } from 'src/app/infrastructure/animations/show-animation';
import { tabMode, TTabModeDeleted } from 'src/app/infrastructure/models/tab-mode';

// Per rij: is er paneel-inhoud (alleen dan een uitklapknop), en welke tekst staat op die knop (bv. "2").
export type TExpandableOf = (item: ILevelGeneralListData) => boolean;
export type TToggleTextOf = (item: ILevelGeneralListData) => string | null;
// Slepen: mag deze rij versleept worden, en binnen welke groep (bv. dezelfde ouder in een boom). null = één groep.
export type TSortableOf = (item: ILevelGeneralListData) => boolean;
export type TSortGroupOf = (item: ILevelGeneralListData) => string | null;

// Na een sleepactie: de versleepte rij en de getoonde lijst in de nieuwe volgorde. De lijst zelf verandert pas als de
// feature hem bijwerkt (de rij springt anders terug).
export interface IExpansionListSort {
    item: ILevelGeneralListData;
    list: ILevelGeneralListData[];
}

// Lijst met per rij een uitklapbaar paneel — voor korte lijsten (geen virtual scroll, dus variabele
// rijhoogte mag). Zelfde basis als NavigationList_ToolComponent (_NavigationList_Base_ToolComponent):
// filteren/sorteren, actief/inactief, verwijderd-lijst met herstellen, knoppen, claims en footer werken
// identiek; alleen de weergave verschilt.
//
// Twee acties per rij, bewust gescheiden (geen geneste interactieve elementen):
//   - klik op de rij (titel) = doorklikken, zoals in elke andere lijst;
//   - de chevron-knop rechts = paneel open/dicht (aria-expanded/aria-controls, toetsenbord-bedienbaar).
// Accordeon: hooguit één paneel open — een paneel openen sluit het vorige. Open-toestand op guid,
// zodat filteren of verversen het open paneel niet sluit.
//
// rowToggles: lijsten zonder doorklik-doel (alle gegevens staan in het paneel) — dan klapt een klik op
// de rij zelf het paneel open/dicht i.p.v. onClick te emitten.
//
// sortable: rijen verslepen (cdk drag, zoals de foto-tablist), alleen in de hoofdlijst, zonder zoekfilter en — met
// sortField — alleen als de lijst oplopend op dat veld sorteert (anders klopt de getoonde volgorde niet met de
// opgeslagen). sortableOf/sortGroupOf beperken het per rij; neerzetten kan alleen op de plek van een rij uit dezelfde
// groep. Opslaan doet de feature via onSort.
@Component({
    selector: 'app-expansion-list',
    templateUrl: './expansion-list.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    styleUrls: [
        '../../../../central-page/central-page.scss',
        '../../../list.scss',
        '../../../expansion-row.scss',
        './expansion-list.component.scss'
    ],
    standalone: true,
    imports: [
        AppMaterialModule,
        ButtonActionComponent,
        DragDropModule,
        ListPositionComponent,
        SharedModule,
        TablistFooterComponent,
    ],
    animations: [
        showAnimation,
        expandPanelAnimation,
    ]
})
export class ExpansionList_ToolComponent
    extends _NavigationList_Base_ToolComponent
    implements AfterViewInit, OnInit, OnDestroy
{
    protected override readonly _activatedRouteService = injectRequired(ActivatedRouteService);
    protected override readonly _listService = injectRequired(_ListService);
    protected override readonly _loadingPageService = injectRequired(LoadingPageService);
    protected override readonly _loadingTabService = injectRequired(LoadingTabService);
    protected override readonly _pageService = injectRequired(_PageService);
    protected override readonly _pagerService = injectRequired(PagerService);
    protected override readonly _progressBarService = injectRequired(ProgressBarService);
    protected override readonly _tabDataService = injectRequired(TabDataService);
    protected override readonly _tabListService = injectRequired(TabListService);

    override input_btnAddData$$ = this._input_btnAddData$$;
    override input_btnDeleteData$$ = this._input_btnDeleteData$$;
    override input_buttonsHide$$ = this._input_buttonsHide$$;
    override input_claimPageList$$ = this._input_claimPageList$$;
    override input_itemLines$$ = this._input_itemLines$$;
    override input_itemSize$$ = this._input_itemSize$$;
    override input_noClick$$ = this._input_noClick$$;
    override input_pageNumber$$ = this._input_pageNumber$$;
    override input_pageSize$$ = this._input_pageSize$$;
    override input_template$$ = this._input_template$$;

    override out_onAdd = this._out_onAdd;
    override out_onClick = this._out_onClick;
    override out_onDeleteDeep = this._out_onDeleteDeep;
    override out_onRestore = this._out_onRestore;
    override out_onSave = this._out_onSave;

    override activeGuid$$ = this._activeGuid$$;
    override canClickThrough$$ = this._canClickThrough$$;
    override btnAddData$$ = this._btnAddData$$;
    override btnDeleteData$$ = this._btnDeleteData$$;
    override btnDeleteDeepData$$ = this._btnDeleteDeepData$$;
    override btnRestoreListData$$ = this._btnRestoreListData$$;
    override buttonsHide$$ = this._buttonsHide$$;
    override claimPageList$$ = this._claimPageList$$;
    override filterString$$ = this._filterString$$;
    override itemLines$$ = this._itemLines$$;
    override itemSize$$ = this._itemSize$$;
    override list$$ = this._list$$;
    override listActive$$ = this._listActive$$;
    override listAll$$ = this._listAll$$;
    override listDeleted$$ = this._listDeleted$$;
    override listDeletedAll$$ = this._listDeletedAll$$;
    override listFiltered$$ = this._listFiltered$$;
    override listInactive$$ = this._listInactive$$;
    override loadingPage$$ = this._loadingPage$$;
    override loadingTab$$ = this._loadingTab$$;
    override showSpinner$$ = this._showSpinner$$;
    override matList$$ = this._matList$$;
    override noClick$$ = this._noClick$$;
    override pageNumber$$ = this._pageNumber$$;
    override pageSize$$ = this._pageSize$$;
    // Eigen scroll-container (zie __scrollContainer$$), niet de viewport/lijst uit de basisklasse.
    override scrollElement$$ = computed<HTMLElement | null>(() => this.__scrollContainer$$()?.nativeElement ?? null);
    override scrollPos$$ = this._scrollPos$$;
    override searchFields$$ = this._searchFields$$;
    override sortFields$$ = this._sortFields$$;
    override tabMode$$ = this._model_tabMode$$;
    override template$$ = this._input_template$$;
    override template_buttons$$ = this._view_template_buttons$$;
    override template_footer$$ = this._view_template_footer$$;

    filterMode$$ = this._pagerService.filterMode$$;

    // Paneel-inhoud per rij (context: $implicit = het item), welke rijen uitklapbaar zijn en de tekst op
    // de uitklapknop. Zonder expandableOf is elke rij met een panelTemplate uitklapbaar.
    input_panelTemplate$$ = input<TemplateRef<any> | null>(null, { alias: 'panelTemplate' });
    input_expandableOf$$ = input<TExpandableOf | null>(null, { alias: 'expandableOf' });
    input_toggleTextOf$$ = input<TToggleTextOf | null>(null, { alias: 'toggleTextOf' });
    input_rowToggles$$ = input(false, { transform: booleanAttribute, alias: 'rowToggles' });
    // Lijst zonder herstel (bv. FunctionMembers: heractiveren gaat via de members-sync-checklist):
    // geen restore-knop in de deleted/inactive-modus.
    input_restoreHide$$ = input(false, { transform: booleanAttribute, alias: 'restoreHide' });
    input_sortable$$ = input(false, { transform: booleanAttribute, alias: 'sortable' });
    input_sortField$$ = input<string | null>(null, { alias: 'sortField' });
    input_sortableOf$$ = input<TSortableOf | null>(null, { alias: 'sortableOf' });
    input_sortGroupOf$$ = input<TSortGroupOf | null>(null, { alias: 'sortGroupOf' });

    out_onSort = output<IExpansionListSort>({ alias: 'onSort' });

    // Touch: pas na even vasthouden slepen, zodat scrollen blijft werken (zelfde als de foto-tablist).
    readonly dragStartDelay = { touch: 300, mouse: 0 };
    dragEnabled$$ = computed<boolean>(() => {
        if (!this.input_sortable$$() || !this.mainListShow$$() || !!this.filterString$$()) return false;
        const __field = this.input_sortField$$();
        if (!__field) return true;
        const __first = this.sortFields$$()?.[0];
        return __first?.field === __field && __first.direction === 'asc';
    });
    // Arrow function: cdk roept het predicaat los aan (zonder this).
    readonly sortPredicate = (index: number, drag: CdkDrag<ILevelGeneralListData>, drop: CdkDropList<ILevelGeneralListData[]>): boolean => {
        const __target = drop.data?.[index];
        const __groupOf = this.input_sortGroupOf$$();
        return !!__target && (!__groupOf || __groupOf(__target) === __groupOf(drag.data));
    };

    private readonly __expandedGuid$$ = signal<string | null>(null);
    private readonly __elementRef = injectRequired(ElementRef<HTMLElement>);
    // Duur/verloop uit het thema, gelezen bij elk open-/dichtklappen (zie expandPanel_read).
    protected readonly expandPanel$$ = signal(expandPanel_binding());
    protected readonly _toggleLabel = { expand: 'uitklappen', collapse: 'inklappen' } as const;

    // Eigen scroll-container (geen CdkVirtualScrollViewport): positie bewaren/herstellen via deze.
    private readonly __scrollContainer$$ = viewChild<ElementRef<HTMLElement>>('scrollContainer');

    deletedListShow$$ = computed<boolean>(() => this.tabMode$$() === tabMode.deletedList);
    inactiveListShow$$ = computed<boolean>(() => this.tabMode$$() === tabMode.inactiveList);
    mainListShow$$ = computed<boolean>(() => this.tabMode$$() === tabMode.mainList);
    hasInactiveList$$ = computed<boolean>(() => this.listInactive$$().length > 0);
    hasDeletedList$$ = computed<boolean>(() => this.listDeleted$$().length > 0);

    btnAddShow$$ = computed<boolean>(() => this.btnAddData$$()?.[listButtonData.hide] == false);
    btnDeletedListShow$$ = computed<boolean>(() => this.btnDeleteData$$()?.[listButtonData.hide] == false);
    btnDeleteDeepShow$$ = computed<boolean>(() => this.btnDeleteDeepData$$()?.[listButtonData.hide] == false);
    btnDeleteDeepDisabled$$ = computed<boolean>(() => this.btnDeleteDeepData$$()?.[listButtonData.disabled] == true);
    btnRestoreDisabled$$ = computed<boolean>(() => this.btnRestoreListData$$()?.[listButtonData.disabled] == true);
    btnRestoreListShow$$ = computed<boolean>(() => this.btnRestoreListData$$()?.[listButtonData.hide] == false);

    constructor(
        protected _renderer?: Renderer2
    ) {
        super(_renderer);
    }

    override ngOnInit(): void {
        super._ngOnInit();
    }

    override emit_btnAdd_click(): void {
        super._btnAdd_emit();
    }

    override emit_btnRestore_click(): void {
        super._btnRestore_emit();
    }

    override emit_btnDeleteDeep_click(): void {
        super._btnDeleteDeep_emit();
    }

    override emit_change(event: MatSelectionListChange): void {
        const __item = event.options[0].value;
        const __deleted = __item.deleted;
        __item.deleted = !__deleted;
        this._btnRestore_setEnable(this.list$$().some(i => !i.deleted));
    }

    override emit_item_click(event: MouseEvent, item: ILevelGeneralListDto): void {
        super._emit_item_click(event, item);
    }

    override emit_onFilterChange(): void {
        super._emit_onFilterChange();
    }

    override emit_onScrollEnd(): void {
        this._onScrollEnd();
    }

    override emit_toggleDeleted(mode?: TTabModeDeleted): void {
        super._emit_toggleDeleted(mode);
    }

    override activeItem_set(item: ILevelGeneralListData): void {
        super._activeItem_set(item);
    }

    override isActivated(item: ILevelGeneralListData): boolean {
        return super._isActivated(item);
    }

    override toggleDeleted(mode?: TTabModeDeleted): void {
        super._toggleDeleted(mode);
    }

    override trackById(index: number, item: { guid: string; }): string {
        return super._trackById(item);
    }

    isDraggable(item: ILevelGeneralListData): boolean {
        if (!this.dragEnabled$$()) return false;
        const __of = this.input_sortableOf$$();
        return __of ? __of(item) : true;
    }

    emit_onDrop(event: CdkDragDrop<ILevelGeneralListData[]>): void {
        if (event.previousIndex === event.currentIndex) return;
        const __list = [...event.container.data];
        moveItemInArray(__list, event.previousIndex, event.currentIndex);
        this.out_onSort.emit({ item: event.item.data, list: __list });
    }

    isExpandable(item: ILevelGeneralListData): boolean {
        if (!this.input_panelTemplate$$()) return false;
        const __of = this.input_expandableOf$$();
        return __of ? __of(item) : true;
    }

    isExpanded(item: ILevelGeneralListData): boolean {
        return this.__expandedGuid$$() === item.guid;
    }

    toggleText(item: ILevelGeneralListData): string | null {
        return this.input_toggleTextOf$$()?.(item) ?? null;
    }

    // Uniek id voor aria-controls (guid is al uniek binnen de lijst).
    panelId(item: ILevelGeneralListData): string {
        return `expansion-panel-${item.guid}`;
    }

    // Klik op de rij: doorklikken, of (rowToggles) het paneel open/dicht.
    emit_row_click(event: MouseEvent, item: ILevelGeneralListData): void {
        if (this.input_rowToggles$$()) {
            if (this.isExpandable(item)) this.toggleExpanded(item);
            return;
        }
        this.emit_item_click(event, item as ILevelGeneralListDto);
    }

    toggleExpanded(item: ILevelGeneralListData): void {
        this.expandPanel$$.set(expandPanel_read(this.__elementRef.nativeElement));
        this.__expandedGuid$$.update(guid => guid === item.guid ? null : item.guid);
    }

    protected override _onScrollEnd(): void {
        const __position = Math.round(this.__scrollContainer$$()?.nativeElement.scrollTop ?? 0);
        this._tabDataService.ScrollPos = __position;
    }

    protected override _position_scrollTo(scrollPosition?: number | null): void {
        const __scrollPos = scrollPosition ?? this._tabDataService.ScrollPos ?? 0;
        setTimeout(() => this.__scrollContainer$$()?.nativeElement.scrollTo({ top: __scrollPos }));
    }

    override ngAfterViewInit(): void {
        super._ngAfterViewInit();
    }

    override ngOnDestroy(): void {
        super._ngOnDestroy();
    }
}
